import { LoadingButton } from "@mui/lab";
import { Box, Grid, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { SpinnerOverlay } from "components/molecules/SpinnerOverlay";
import { FormikProvider, useFormik } from "formik";
import { usePaginationRequest } from "hooks";
import { chain, concat, findIndex, isEmpty, map } from "lodash";
import {
  IPaginationRequest,
  IPermission,
  IUpdateRolePermissionRequest,
} from "model";
import { IRole } from "model/Role";
import React, { useEffect, useState } from "react";
import { PermissionActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { RoleApiService } from "services";
import PermissionApiService from "services/PermissionApiService";
import { I18nTranslator, translations } from "utils/i18n";

const UserGuide = () => {
  const dispatch = useAppDispatch();
  const permissionAction = useAppSelector(
    (state) => state.AppConfigReducer.currentAction
  );
  const [refresh, setRefresh] = useState<boolean>();
  const [roleRequest] = React.useState<IPaginationRequest>({});
  const [permissionRequest] = React.useState<IPaginationRequest>({
    pageSize: 100000,
    pageNumber: 1,
  });
  const [permissionByRoleRequest, setPermissionByRoleRequest] =
    React.useState<any>({
      pageSize: 100000,
      pageNumber: 1,
    });

  const [currentPermissionByRole, setCurrentPermissionByRole] =
    useState<string[]>();

  const [currentRoleId, setCurrentRoleId] = React.useState<string>("");
  const { loading: roleLoading, result: roles } = usePaginationRequest<
    IPaginationRequest,
    IRole[]
  >(roleRequest, RoleApiService.getAllRoles);

  const { loading: permissionLoading, result: permissions } =
    usePaginationRequest<IPaginationRequest, IPermission[]>(
      permissionRequest,
      PermissionApiService.getAllPermissions
    );

  const { loading: permissionByRoleLoading, result: permissionByRole } =
    usePaginationRequest<any, IPermission[]>(
      permissionByRoleRequest,
      PermissionApiService.getPermissionByRole
    );

  useEffect(() => {
    if (!isEmpty(roles)) {
      setCurrentRoleId(roles.data![0].id || "");
      setPermissionByRoleRequest({
        ...permissionByRoleRequest,
        roleId: roles.data![0].id || "",
      });
    }
  }, [roles.data]);
  useEffect(() => {
    if (!isEmpty(permissionByRole)) {
      setCurrentPermissionByRole(
        permissionByRole.data!.map((e) => e.permission)
      );
    }
  }, [permissionByRole]);

  useEffect(() => {
    if (
      permissionAction.includes(
        PermissionActions.updateRolePermissions.successName
      )
    ) {
      setCurrentPermissionByRole(
        permissionByRole.data!.map((e) => e.permission)
      );
    }
  }, [permissionAction]);

  const getPermission = () => {
    return (
      chain(
        map(permissions.data, (e) => {
          let key = e.permission.split(".");
          return {
            key: key[1],
            type: key[2],
            permission: e.permission,
          };
        })
      )
        .groupBy("key")
        .map((value, key) => ({
          key,
          value,
        }))
        .value() || []
    );
  };

  const formik = useFormik({
    initialValues: {
      permissions: currentPermissionByRole,
    },
    enableReinitialize: true,
    onSubmit: ({ permissions }) => {
      const request: IUpdateRolePermissionRequest = {
        roleId: currentRoleId,
        permissions:
          permissions?.map((e) => {
            return {
              permission: e,
            };
          }) || [],
      };
      dispatch(PermissionActions.updateRolePermissions.request(request));
    },
  });

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit}>
        <Box sx={{ width: "100%" }}>
          <Typography variant="h2" sx={{ paddingBottom: "20px" }}>
            {I18nTranslator(translations.settings.permission)}
          </Typography>
          <Box sx={{ paddingTop: "10px" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ minWidth: "300px" }}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel id="demo-simple-select-label">
                    {I18nTranslator(translations.settings.role)}
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    value={currentRoleId}
                    label={I18nTranslator(translations.settings.role)}
                    variant="outlined"
                    onChange={(event) => {
                      setCurrentRoleId(event.target.value as string);
                      setPermissionByRoleRequest({
                        ...permissionByRoleRequest,
                        roleId: event.target.value as string,
                      });
                    }}
                  >
                    {roles &&
                      roles?.data?.map((role) => {
                        return <MenuItem value={role.id}>{role.name}</MenuItem>;
                      })}
                  </Select>
                </FormControl>
              </Box>
              <LoadingButton
                loading={permissionAction.includes(
                  PermissionActions.updateRolePermissions.requestName
                )}
                variant="contained"
                onClick={formik.submitForm}
              >
                {I18nTranslator(translations.button.save)}
              </LoadingButton>
            </Box>

            <Box sx={{ paddingTop: "20px" }}>
              <Typography fontSize={22} fontWeight={600}>
                {I18nTranslator(translations.settings.permissionList)}
              </Typography>

              <Grid container rowSpacing={2}>
                <Grid xs={12} container item rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={4}>
                    <Box
                      sx={{
                        aspectRatio: 1,
                        backgroundColor: "green",
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={2}
                    sx={{ borderLeftColor: "red", borderLeftWidth: 1 }}
                  >
                    <Box
                      sx={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <Typography fontWeight={500} fontSize={20}>
                        {I18nTranslator(translations.settings.view)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Box
                      sx={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <Typography fontWeight={500} fontSize={20}>
                        {I18nTranslator(translations.settings.create)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Box
                      sx={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <Typography fontWeight={500} fontSize={20}>
                        {I18nTranslator(translations.settings.update)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    <Box
                      sx={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <Typography fontWeight={500} fontSize={20}>
                        {I18nTranslator(translations.settings.delete)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {permissionLoading ? (
                  <SpinnerOverlay show />
                ) : (
                  getPermission().map((value) => {
                    return (
                      <Grid
                        xs={12}
                        container
                        item
                        rowSpacing={2}
                        columnSpacing={2}
                      >
                        <Grid item xs={4}>
                          <Typography fontWeight={500} fontSize={16}>
                            {value.key}
                          </Typography>
                        </Grid>
                        {value.value.map((e) => {
                          return (
                            <Grid
                              item
                              xs={2}
                              sx={{
                                borderLeftColor: "red",
                                borderLeftWidth: 1,
                              }}
                            >
                              <Checkbox
                                sx={{ textAlign: "center" }}
                                checked={
                                  formik.values.permissions?.includes(
                                    e.permission
                                  ) || false
                                }
                                onClick={() => {
                                  const index = findIndex(
                                    formik.values.permissions,
                                    (permission) => permission === e.permission
                                  );

                                  if (index === -1) {
                                    formik.setFieldValue(
                                      "permissions",
                                      concat(formik.values.permissions, [
                                        e.permission,
                                      ])
                                    );
                                  } else {
                                    formik.setFieldValue(
                                      "permissions",
                                      formik.values.permissions?.filter(
                                        (permission) =>
                                          permission !== e.permission
                                      )
                                    );
                                  }
                                }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    );
                  })
                )}
              </Grid>
            </Box>
          </Box>
        </Box>
      </form>
    </FormikProvider>
  );
};

export default UserGuide;
